// Per-record detail tab strip for supplier-detail.vue (Almondco - 2116 - Grower
// Master, technical-design.md §6/AD-04). Mirrors products-tabs.js's real,
// confirmed implementation exactly - a module-level mutable visibility
// variable plus a setter, consumed by gc-header-toolbar.vue's own
// visibleTabs computed (that is where .visible is actually read, not
// tabAccessControl, which only filters by applicationFeatureCode).
//
// Deliberately separate from suppliers-tabs.js, which is the unrelated
// *list*-screen tab bar (supplier-list.vue/awaiting-payment-list.vue/
// batch-payment-list.vue/supplier-transaction.vue) - the same list/detail
// naming split the products/ tabdata folder already has
// (products-list-tabs.js vs products-tabs.js).
import { SettingsService } from "../../../_services/api/settings/graycorp/settings.service";

let growerTabVisible = false;
// Bug fix (ADO #78320, 2026-09-16): this tab previously had no visibility
// flag of its own - grower-intake-estimate.vue's IsGrowerTabVisible() result
// was being reported back under text: "Grower", silently overwriting the
// Grower tab's own flag instead of gating this one. Same SupplierTypesForGrower
// setting, own flag, per the PO's 3-condition gate for this tab.
let growerIntakeEstimatesTabVisible = false;

// The Grower and Grower Intake Estimates tabs are ALWAYS the same visibility
// (both gated by the identical SupplierTypesForGrower setting - "no new
// ApplicationFeatureSetting" per the PO's own note). The six existing call
// sites (supplier-detail.vue, grower-tab.vue, supplier-contact.vue,
// supplier-note.vue, supplier-transaction-history.vue,
// supplier-product-list.vue) each independently recompute only the "Grower"
// signal on their own load - mirroring it onto both flags here, rather than
// adding a second call at every one of those sites, keeps both tabs correct
// no matter which tab the user lands on first.
let SupplierTypesForGrower = undefined;

function SupplierDetailTabs(id) {
  return [
    {
      text: "Details",
      path: `/supplier/review/${id}`,
      applicationFeatureCode: "Customer",
    },
    {
      text: "Contacts",
      path: `/supplier-contact/${id}`,
      applicationFeatureCode: "Customer",
    },
    {
      text: "Notes",
      path: `/supplier-note/${id}`,
      applicationFeatureCode: "Customer",
    },
    {
      text: "Products",
      path: `/supplier-product/${id}`,
      applicationFeatureCode: "Product",
    },
    {
      text: "Transactions",
      path: `/supplier-transaction-history/${id}`,
      applicationFeatureCode: "SupplierTransactionHistory",
    },
    // Permission code repointed to its own dedicated AlmondcoGrower (ADO
    // #78320, 2026-09-16) - previously reused the base Supplier code per the
    // Feature Vision (technical-design.md §11). SupplierTypesForGrower
    // visibility gate (growerTabVisible) is unchanged by this repoint.
    {
      text: "Grower",
      path: `/supplier-grower/${id}`,
      applicationFeatureCode: "AlmondcoGrower",
      visible: growerTabVisible,
    },
    // Permission code repointed to its own dedicated
    // AlmondcoGrowerIntakeEstimates (ADO #78320, 2026-09-16) - previously
    // reused the base Supplier code.
    {
      text: "Grower Intake Estimates",
      path: `/grower-intake-estimate/${id}`,
      applicationFeatureCode: "AlmondcoGrowerIntakeEstimates",
      visible: growerIntakeEstimatesTabVisible,
    },
  ];
}

// Bug fix (#78583, 2026-09-17): must index into the SAME raw array the
// dxTabs widget actually uses for selectedIndex - gc-header-toolbar.vue's
// own visibleTabs computed only mutates inaccessible tabs' `.visible` flag,
// it never removes them from the array (confirmed against DevExtreme's own
// CollectionWidget edit strategy - getItemDataByIndex/getIndexByItemData
// both index the raw, unfiltered `items` option). tabAccessControl actually
// REMOVES inaccessible tabs, so wrapping this lookup with it returned an
// index into a shorter array than the widget's own - whenever any tab
// earlier in the strip (e.g. Transactions, if the user lacks
// SupplierTransactionHistory) failed the permission check, every later
// tab's computed index was off by the number of tabs removed before it,
// pointing the active-tab underline at the wrong (or a hidden) tab.
function GetIndexByTabName(tabName, id) {
  return SupplierDetailTabs(id).findIndex((x) => x.text == tabName);
}

function SupplierDetailTabsVisibility(obj) {
  if (obj && (obj.text == "Grower" || obj.text == "Grower Intake Estimates")) {
    growerTabVisible = obj.value;
    growerIntakeEstimatesTabVisible = obj.value;
  }
}

// Shared with every route that lands directly on one of this record's tabs
// (grower-tab.vue, and supplier-detail.vue itself) - each must independently
// recompute this on its own created(), the same way every product-detail.vue
// sibling tab (product-cost.vue, product-location.vue, etc.) independently
// calls ProductTabsVisibility rather than relying on product-detail.vue
// having run first. SupplierTypesForGrower is a comma-separated list of
// qualifying SUPTYPE codes; a blank/unset value means "tab never visible",
// not "visible for every Supplier Type" (an empty-string split would
// otherwise match every type).
async function IsGrowerTabVisible(supplierTypeCode) {

  if (!SupplierTypesForGrower) {
    const settingResponse = await SettingsService.getBySettingName(
      "SupplierTypesForGrower"
    );

    // getBySettingName() only filters by SettingName, not the full
    // ApplicationFeatureSetting key - narrow to this setting's real
    // ApplicationFeatureCode here so an unrelated same-named row elsewhere
    // can't be picked up non-deterministically (technical-design.md §10).
    const matchingSetting = settingResponse.data.value.find(
      (s) => s.ApplicationFeatureCode === "Supplier"
    );

    SupplierTypesForGrower = matchingSetting;
  }

  const settingValue = SupplierTypesForGrower ? SupplierTypesForGrower.SettingValue : "";
  const qualifyingSupplierTypes = settingValue
    ? settingValue
      .split(",")
      .map((t) => t.trim().toUpperCase())
      .filter((t) => t)
    : [];
  return qualifyingSupplierTypes.includes(
    (supplierTypeCode || "").toUpperCase()
  );
}

export {
  SupplierDetailTabs,
  GetIndexByTabName,
  SupplierDetailTabsVisibility,
  IsGrowerTabVisible,
};
